/**
 * PresetGallery.tsx
 *
 * Galeria de presets prontos com preview de vídeo.
 * Aberta pelo botão "Presets" no header do TakeEditorModal.
 *
 * Uso no TakeEditorModal — substitua o dropdown de presets existente por:
 *
 *   import { PresetGallery } from './PresetGallery'
 *
 *   const [showGallery, setShowGallery] = useState(false)
 *
 *   {showGallery && (
 *     <PresetGallery
 *       savedPresets={savedList}
 *       onSelectBuiltin={(preset) => { setPreset(preset); setShowGallery(false) }}
 *       onSelectSaved={(saved) => { handleLoad(saved); setShowGallery(false) }}
 *       onDeleteSaved={handleDelete}
 *       onClose={() => setShowGallery(false)}
 *     />
 *   )}
 */

'use client'

import { useState, useRef } from 'react'
import { X, Play, Pause, Clock, Layers, Trash2, CloudOff } from 'lucide-react'
import { BUILTIN_PRESETS, type BuiltinPreset } from './builtin-presets'
import type { AnimationPreset } from './take-types'
import type { SavedPreset } from './supabase-presets'
import { createPortal } from 'react-dom'

interface PresetGalleryProps {
  savedPresets: SavedPreset[]
  onSelectBuiltin: (preset: AnimationPreset) => void
  onSelectSaved: (saved: SavedPreset) => void
  onDeleteSaved: (id: string) => void
  onClose: () => void
}

// ─── VideoCard ────────────────────────────────────────────────────────────────

function BuiltinCard({
  item, onSelect,
}: {
  item: BuiltinPreset
  onSelect: () => void
}) {
  const [playing, setPlaying] = useState(true)  // começa tocando (autoPlay)
  const [hovered, setHovered] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)

  // Aumenta velocidade de reprodução ao montar
  const handleVideoLoad = () => {
    if (videoRef.current) videoRef.current.playbackRate = 1.75
  }

  const togglePlay = (e: React.MouseEvent) => {
    e.stopPropagation()
    const v = videoRef.current
    if (!v) return
    if (playing) { v.pause(); setPlaying(false) }
    else { v.play().catch(() => {}); setPlaying(true) }
  }

  const handleVideoEnd = () => setPlaying(false)

  return (
    <div
      onClick={onSelect}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: '#0f172a',
        border: `1px solid ${hovered ? item.accentColor : '#1e293b'}`,
        borderRadius: 10,
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'border-color 0.2s, transform 0.15s',
        transform: hovered ? 'translateY(-2px)' : 'none',
        boxShadow: hovered ? `0 8px 24px ${item.accentColor}22` : 'none',
      }}
    >
      {/* Thumbnail / Vídeo */}
      <div style={{
        position: 'relative',
        aspectRatio: '16/9',
        background: '#080e1a',
        overflow: 'hidden',
      }}>
        {item.videoUrl ? (
          <video
            ref={videoRef}
            src={item.videoUrl}
            muted
            autoPlay
            loop
            playsInline
            onLoadedData={handleVideoLoad}
            onEnded={handleVideoEnd}
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        ) : (
          /* Placeholder quando não tem vídeo */
          <div style={{
            width: '100%', height: '100%',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: 8,
            background: `linear-gradient(135deg, #0f172a, ${item.accentColor}22)`,
          }}>
            <div style={{
              width: 48, height: 48, borderRadius: '50%',
              background: `${item.accentColor}33`,
              border: `2px solid ${item.accentColor}66`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Play size={18} color={item.accentColor} />
            </div>
            <span style={{ fontSize: 10, color: '#475569', display: 'flex', alignItems: 'center', gap: 4 }}>
              <CloudOff size={10} /> sem demo
            </span>
          </div>
        )}

        {/* Overlay hover — clique para pausar/retomar */}
        {item.videoUrl && hovered && (
          <button
            onClick={togglePlay}
            style={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(0,0,0,0.25)',
              border: 'none', cursor: 'pointer',
            }}
          >
            <div style={{
              width: 36, height: 36, borderRadius: '50%',
              background: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(4px)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: '1px solid rgba(255,255,255,0.3)',
            }}>
              {playing
                ? <Pause size={14} color="#fff" />
                : <Play  size={14} color="#fff" style={{ marginLeft: 2 }} />
              }
            </div>
          </button>
        )}

        {/* Accent bar */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          height: 3, background: item.accentColor, opacity: hovered ? 1 : 0,
          transition: 'opacity 0.2s',
        }} />
      </div>

      {/* Info */}
      <div style={{ padding: '10px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#e2e8f0' }}>{item.label}</span>
          <button
            onClick={onSelect}
            style={{
              fontSize: 11, fontWeight: 600,
              background: hovered ? item.accentColor : '#1e293b',
              color: hovered ? '#fff' : '#94a3b8',
              border: 'none', borderRadius: 5,
              padding: '3px 10px', cursor: 'pointer',
              transition: 'background 0.2s, color 0.2s',
            }}
          >
            Usar
          </button>
        </div>
        <p style={{ fontSize: 11, color: '#64748b', marginBottom: 8, lineHeight: 1.4 }}>
          {item.description}
        </p>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {item.tags.map(tag => (
            <span key={tag} style={{
              fontSize: 9, color: '#475569',
              background: '#1e293b', borderRadius: 20,
              padding: '2px 8px', fontFamily: 'monospace',
            }}>
              {tag}
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}

// ─── SavedCard ────────────────────────────────────────────────────────────────

function SavedCard({
  saved, onSelect, onDelete,
}: {
  saved: SavedPreset
  onSelect: () => void
  onDelete: () => void
}) {
  const [hovered, setHovered] = useState(false)
  const takes = saved.preset?.takes?.length ?? 0
  const dur = saved.preset?.takes?.reduce((m: number, t: any) => Math.max(m, t.endTime), 0) ?? 0

  return (
    <div
      onClick={onSelect}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: '#0f172a',
        border: `1px solid ${hovered ? '#6366f1' : '#1e293b'}`,
        borderRadius: 10, padding: '12px 14px',
        cursor: 'pointer', display: 'flex',
        alignItems: 'center', gap: 12,
        transition: 'border-color 0.2s',
      }}
    >
      <div style={{
        width: 40, height: 40, borderRadius: 8, flexShrink: 0,
        background: '#1e293b',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Layers size={18} color="#6366f1" />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {saved.name}
        </div>
        <div style={{ fontSize: 10, color: '#475569', display: 'flex', gap: 10, marginTop: 2 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Clock size={9} />{dur}s
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Layers size={9} />{takes} takes
          </span>
          <span>{new Date(saved.updated_at).toLocaleDateString('pt-BR')}</span>
        </div>
      </div>

      <button
        onClick={e => { e.stopPropagation(); onDelete() }}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: '#334155', padding: 6, borderRadius: 6,
          transition: 'color 0.15s',
        }}
        onMouseEnter={e => (e.currentTarget.style.color = '#ef4444')}
        onMouseLeave={e => (e.currentTarget.style.color = '#334155')}
        title="Deletar"
      >
        <Trash2 size={13} />
      </button>
    </div>
  )
}

// ─── Main Gallery ─────────────────────────────────────────────────────────────

export function PresetGallery({
  savedPresets, onSelectBuiltin, onSelectSaved, onDeleteSaved, onClose,
}: PresetGalleryProps) {
  const [tab, setTab] = useState<'builtin' | 'saved'>('builtin')

  const content = (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{
        background: '#0d1117',
        border: '1px solid #1e293b',
        borderRadius: 14, width: '100%', maxWidth: 860,
        maxHeight: '88vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 32px 100px rgba(0,0,0,0.8)',
        overflow: 'hidden',
      }}>

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 20px', borderBottom: '1px solid #1e293b',
          background: 'linear-gradient(135deg,#0f172a,#1a1a2e)',
        }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#e2e8f0' }}>Presets de Animação</div>
            <div style={{ fontSize: 11, color: '#475569', marginTop: 1 }}>Escolha um ponto de partida ou carregue um salvo</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#475569', padding: 6 }}>
            <X size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #1e293b', padding: '0 20px' }}>
          {([['builtin', 'Prontos'], ['saved', `Salvos${savedPresets.length ? ` (${savedPresets.length})` : ''}`]] as const).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '10px 16px', fontSize: 13, fontWeight: 600,
                color: tab === key ? '#e2e8f0' : '#475569',
                borderBottom: `2px solid ${tab === key ? '#6366f1' : 'transparent'}`,
                marginBottom: -1, transition: 'color 0.15s',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>

          {tab === 'builtin' && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
              gap: 16,
            }}>
              {BUILTIN_PRESETS.map(item => (
                <BuiltinCard
                  key={item.id}
                  item={item}
                  onSelect={() => onSelectBuiltin(item.preset)}
                />
              ))}
            </div>
          )}

          {tab === 'saved' && (
            savedPresets.length === 0 ? (
              <div style={{
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                height: 200, gap: 12, color: '#334155',
              }}>
                <Layers size={32} />
                <span style={{ fontSize: 13 }}>Nenhum preset salvo ainda</span>
                <span style={{ fontSize: 11, color: '#1e293b' }}>Edite um preset e clique em Salvar</span>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {savedPresets.map(s => (
                  <SavedCard
                    key={s.id}
                    saved={s}
                    onSelect={() => onSelectSaved(s)}
                    onDelete={() => onDeleteSaved(s.id)}
                  />
                ))}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  )

  return createPortal(content, document.body)
}